import * as React from "react";
import { useSearchParams } from "react-router";

import { ROBOQL_SEARCH_PARAM } from "@/shared/domain/query/roboqlSearchParam";

import type { ActiveFilters } from "../Filters/FilterModel";

/**
 * Drop `?query=` from the URL on the first change to the table's filters, so
 * the parameter only ever describes what is on screen. Call it from a list
 * under a `RoboqlLinkGate`, with `view.filters`.
 *
 * The filters the list mounted with are the baseline, whatever seeded them: the
 * link's query, nothing (a query that failed to parse), or a `?view=` that won
 * over it. Any change from there means the link no longer describes the table.
 *
 * Writes once. `setSearchParams` builds on the params of the render that called
 * it, so a second write could undo a `?view=` the picker wrote in between.
 */
export function useRoboqlLinkSync(filters: ActiveFilters): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const [mountFilters] = React.useState(filters);
  const droppedRef = React.useRef(false);
  const paramInUrl = searchParams.has(ROBOQL_SEARCH_PARAM);

  React.useEffect(() => {
    if (droppedRef.current || !paramInUrl || filters === mountFilters) {
      return;
    }
    droppedRef.current = true;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(ROBOQL_SEARCH_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [filters, mountFilters, paramInUrl, setSearchParams]);
}
